import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

interface PasswordChangeFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (oldPassword: string | null, newPassword: string, newPasswordConfirm: string) => Promise<void>;
  userName: string;
  isSelfChange?: boolean;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  open,
  onClose,
  onSubmit,
  userName,
  isSelfChange = true,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordRequirements: PasswordRequirement[] = [
    { label: 'At least 8 characters', test: pwd => pwd.length >= 8 },
    { label: 'At least 1 uppercase letter', test: pwd => /[A-Z]/.test(pwd) },
    { label: 'At least 1 lowercase letter', test: pwd => /[a-z]/.test(pwd) },
    { label: 'At least 1 number', test: pwd => /\d/.test(pwd) },
    { label: 'At least 1 special character', test: pwd => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
  ];

  const getPasswordStrength = (pwd: string): number => {
    return passwordRequirements.filter(req => req.test(pwd)).length;
  };

  const getPasswordStrengthColor = (strength: number): 'error' | 'warning' | 'success' => {
    if (strength < 3) return 'error';
    if (strength < 5) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength < 3) return 'Weak';
    if (strength < 5) return 'Medium';
    return 'Strong';
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) setError(null);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (error) setError(null);
  };

  const validateForm = () => {
    if (isSelfChange) {
      if (!oldPassword) {
        setError('Current password is required');
        return false;
      }
      if (oldPassword === password) {
        setError('New password should be different from current password');
        return false;
      }
    }
    if (!password) {
      setError('New password is required');
      return false;
    }
    const strength = getPasswordStrength(password);
    if (strength < 3) {
      setError('Password is too weak. Please meet at least 3 requirements.');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setError(null);
    try {
      await onSubmit(isSelfChange ? oldPassword : null, password, confirmPassword);
      setOldPassword('');
      setPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    if (!isLoading) {
      setOldPassword('');
      setPassword('');
      setConfirmPassword('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Password for {userName}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Only show old password input if self-change */}
            {isSelfChange && (
              <TextField
                fullWidth
                margin="dense"
                label="Current Password"
                type="password"
                value={oldPassword}
                onChange={e => {
                  setOldPassword(e.target.value);
                  if (error) setError(null);
                }}
                disabled={isLoading}
                required
                autoComplete="current-password"
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              required
              fullWidth
              label="New Password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              disabled={isLoading}
              error={password !== '' && getPasswordStrength(password) < 3}
            />

            {password && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Password Strength:
                  </Typography>
                  <Typography
                    variant="body2"
                    color={`${getPasswordStrengthColor(getPasswordStrength(password))}.main`}
                    fontWeight="bold"
                  >
                    {getPasswordStrengthText(getPasswordStrength(password))}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(getPasswordStrength(password) / passwordRequirements.length) * 100}
                  color={getPasswordStrengthColor(getPasswordStrength(password))}
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Password Requirements:
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {passwordRequirements.map((req, index) => (
                    <ListItem key={index} sx={{ py: 0, minHeight: 24 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        {req.test(password) ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Cancel color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: req.test(password) ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <TextField
              required
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              disabled={isLoading}
              error={password !== confirmPassword && confirmPassword !== ''}
              helperText={password !== confirmPassword && confirmPassword !== '' ? 'Passwords do not match' : ''}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              isLoading ||
              !password ||
              !confirmPassword ||
              password !== confirmPassword ||
              getPasswordStrength(password) < 3 ||
              (isSelfChange && !oldPassword)
            }
          >
            {isLoading ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PasswordChangeForm;
